import { Layer, Popup, Source } from "@vis.gl/react-maplibre";
import type {
  DataDrivenPropertyValueSpecification,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
} from "maplibre-gl";
import { FC, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  MAPLIBRE_POLYGON_TOOLTIP_CLASS,
  MAPLIBRE_POLYGON_TOOLTIP_OFFSET_PX,
  MAPLIBRE_POLYGONS_HOVER_NO_MATCH_ID,
} from "./maplibre-polygons-layer.constants";
import {
  IHoveredPolygon,
  IMapLibrePolygonFeatureProperties,
  IMapLibrePolygonsLayerProps,
} from "./maplibre-polygons-layer.models";
import {
  buildMapLibrePolygonsFeatureCollection,
  resolveMapLibrePolygonsStyle,
} from "./maplibre-polygons-layer.utils";
import { useMapLibreLayerPointer } from "./use-maplibre-layer-pointer";

/**
 * Area / zone polygons on MapLibre — the twin of the Leaflet `L.geoJSON` groups behind the areas map, the
 * stations map's area overlay and the polygon viewer.
 *
 * Every polygon shares one source and two layers (fill + outline) with data-driven paint, rather than a
 * layer pair per polygon as Leaflet had. Hover restyling swaps the paint values for the hovered id through
 * a `case` expression, so the whole interaction stays on the GPU.
 */
export const MapLibrePolygonsLayer: FC<IMapLibrePolygonsLayerProps> = ({
  id,
  polygons,
  style,
  isHoverEnabled,
  onPolygonClick,
}) => {
  const [hovered, setHovered] = useState<IHoveredPolygon | null>(null);
  // Hover highlight and tooltip normally follow clickability, as they did on Leaflet where both came from
  // the same non-dimmed layer. The areas map separates them: without update permission the polygons are
  // not clickable, but they still name themselves on hover.
  const isInteractive = isHoverEnabled ?? Boolean(onPolygonClick);
  const resolvedStyle = useMemo(
    () => resolveMapLibrePolygonsStyle(style),
    [style],
  );

  const featureCollection = useMemo(
    () =>
      buildMapLibrePolygonsFeatureCollection({
        polygons,
        style: resolvedStyle,
        isInteractive,
      }),
    [polygons, resolvedStyle, isInteractive],
  );

  const fillLayerId = `${id}-fill`;
  const hasFeatures = featureCollection.features.length > 0;

  const readProperties = (
    feature: MapGeoJSONFeature,
  ): IMapLibrePolygonFeatureProperties | null => {
    const properties = feature.properties as
      IMapLibrePolygonFeatureProperties | undefined;

    // `isInteractive` rides on the feature so a dimmed polygon under the cursor stays inert instead of
    // swallowing the hover — Leaflet got this from `interactive: false` on the dimmed layer.
    return properties?.isInteractive ? properties : null;
  };

  const handleHover = useEvent(
    (feature: MapGeoJSONFeature, event: MapLayerMouseEvent) => {
      const properties = readProperties(feature);

      if (!properties) {
        setHovered(null);
        return;
      }

      // Tracks the cursor, not the polygon's centre — Leaflet's `sticky: true` tooltip did the same.
      setHovered({
        id: properties.polygonId,
        tooltip: properties.tooltip,
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      });
    },
  );

  const handleLeave = useEvent(() => setHovered(null));

  const handleClick = useEvent((feature: MapGeoJSONFeature) => {
    const properties = readProperties(feature);

    if (properties) {
      onPolygonClick?.(properties.polygonId);
    }
  });

  useMapLibreLayerPointer({
    layerId: fillLayerId,
    enabled: isInteractive && hasFeatures,
    onHover: handleHover,
    onLeave: handleLeave,
    onClick: handleClick,
  });

  if (!hasFeatures) {
    return null;
  }

  // `["get", …]` reads each polygon's own value off the feature; the `case` swaps in the hover values for
  // the polygon under the cursor. With nothing hovered the id is a sentinel no real polygon can match, so
  // the resting branch wins.
  const hoveredId = hovered?.id ?? MAPLIBRE_POLYGONS_HOVER_NO_MATCH_ID;
  const hoverAware = (
    restingProperty: string,
    hoverValue: number,
  ): DataDrivenPropertyValueSpecification<number> =>
    [
      "case",
      ["==", ["get", "polygonId"], hoveredId],
      hoverValue,
      ["get", restingProperty],
    ] as DataDrivenPropertyValueSpecification<number>;

  return (
    <>
      <Source id={id} type="geojson" data={featureCollection}>
        <Layer
          id={fillLayerId}
          type="fill"
          paint={{
            ["fill-color"]: ["get", "color"],
            ["fill-opacity"]: hoverAware(
              "fillOpacity",
              resolvedStyle.hoverFillOpacity,
            ),
          }}
        />
        <Layer
          id={`${id}-outline`}
          type="line"
          paint={{
            ["line-color"]: ["get", "color"],
            ["line-opacity"]: ["get", "strokeOpacity"],
            ["line-width"]: hoverAware("weight", resolvedStyle.hoverWeight),
          }}
        />
      </Source>

      {hovered && Boolean(hovered.tooltip) && (
        <Popup
          longitude={hovered.lng}
          latitude={hovered.lat}
          anchor="bottom"
          offset={MAPLIBRE_POLYGON_TOOLTIP_OFFSET_PX}
          closeButton={false}
          closeOnClick={false}
          className={MAPLIBRE_POLYGON_TOOLTIP_CLASS}
        >
          {hovered.tooltip}
        </Popup>
      )}
    </>
  );
};
